import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface CollaborationStep {
  number: string;
  title: string;
  description: string;
  icon: string;
}

const CollaborationModelSection = () => {
  const steps: CollaborationStep[] = [
    {
      number: '01',
      title: 'Application & Qualification',
      description: 'Submit your application with credentials, experience, and areas of expertise. Our team reviews within 5 business days.',
      icon: 'DocumentTextIcon'
    },
    {
      number: '02',
      title: 'Discovery Interview',
      description: 'Meet with our partnership team to discuss your practice, client focus, and collaboration interests.',
      icon: 'ChatBubbleLeftRightIcon'
    },
    {
      number: '03',
      title: 'Methodology Training',
      description: 'Complete our AI Change Leadership certification program covering frameworks, tools, and delivery approaches.',
      icon: 'AcademicCapIcon'
    },
    {
      number: '04',
      title: 'Network Onboarding',
      description: 'Join our partner community, access client opportunities, and begin collaborative delivery engagements.',
      icon: 'UserGroupIcon'
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-card">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold font-headline text-primary mb-6">
            Our Collaboration Model
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            A structured pathway from application to active partnership, designed to ensure quality and mutual success.
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative bg-background rounded-xl p-8 shadow-card hover:shadow-modal transition-all duration-300 border border-border"
              >
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-secondary rounded-full flex items-center justify-center shadow-strong">
                  <span className="text-2xl font-bold font-headline text-secondary-foreground">
                    {step.number}
                  </span>
                </div>
                
                <div className="ml-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name={step.icon as any} size={24} variant="solid" className="text-secondary" />
                    <h3 className="text-xl font-bold font-headline text-primary">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground font-body leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 bg-primary/5 rounded-xl p-8 border border-primary/20">
            <div className="flex items-start gap-4">
              <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-primary flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-lg font-bold font-headline text-primary mb-2">
                  Partnership Timeline
                </h4>
                <p className="text-foreground font-body">
                  From application to active partnership typically takes 4-6 weeks. We maintain high standards to ensure network quality and partner success. All partners receive ongoing support, methodology updates, and access to our collaborative delivery platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollaborationModelSection;